import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  Boxes,
  ChevronDown,
  ChevronRight,
  Code,
  Database,
  Menu,
  Search,
  Server,
  X,
} from "lucide-react";
import {
  getDocsIndex,
  getDocsSection,
  type DocsEndpoint,
  type DocsIndex,
  type DocsModel,
  type DocsSection,
  type DocsSectionSummary,
  type DocsService,
} from "../../api/docs";

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export default function DocsPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const indexQ = useQuery({
    queryKey: ["docs", "index"],
    queryFn: getDocsIndex,
    staleTime: 60_000,
  });

  // первичный выбор раздела — первый api-раздел
  const effectiveSelected = useMemo(() => {
    if (selected) return selected;
    const first = indexQ.data?.sections.find((s) => s.kind === "api");
    return first?.id ?? indexQ.data?.sections[0]?.id ?? null;
  }, [selected, indexQ.data]);

  const sectionQ = useQuery({
    queryKey: ["docs", "section", effectiveSelected],
    queryFn: () => getDocsSection(effectiveSelected as string),
    enabled: !!effectiveSelected,
    staleTime: 60_000,
  });

  // фильтрация sidebar по поисковой строке (по имени раздела и эндпойнтам)
  const filteredSections = useMemo(() => {
    if (!indexQ.data) return [];
    const t = query.trim().toLowerCase();
    if (!t) return indexQ.data.sections;
    const matchedTags = new Set<string>();
    for (const ep of indexQ.data.endpoints) {
      if (
        ep.path.toLowerCase().includes(t) ||
        ep.summary.toLowerCase().includes(t) ||
        ep.operation_id.toLowerCase().includes(t)
      ) {
        matchedTags.add(`api:${ep.tag}`);
      }
    }
    return indexQ.data.sections.filter(
      (s) => s.title.toLowerCase().includes(t) || matchedTags.has(s.id),
    );
  }, [indexQ.data, query]);

  const onSelect = (id: string) => {
    setSelected(id);
    setDrawerOpen(false);
  };

  return (
    <div className="flex min-h-[60vh] flex-col">
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Документация платформы
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            {indexQ.data
              ? `${indexQ.data.api_title} · v${indexQ.data.api_version} · сгенерировано ${formatGenerated(indexQ.data.generated_at)}`
              : "Загрузка…"}
          </div>
        </div>
        <button
          className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2 text-[13px] font-medium text-[var(--color-fg)] transition-all hover:-translate-y-px hover:border-[var(--color-fg-muted)] lg:hidden"
          onClick={() => setDrawerOpen(true)}
        >
          <Menu size={14} className="opacity-70" />
          Разделы
        </button>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block">
          <SidebarCard
            data={indexQ.data}
            sections={filteredSections}
            selectedId={effectiveSelected}
            onSelect={onSelect}
            query={query}
            onQuery={setQuery}
          />
        </aside>

        {/* Drawer mobile */}
        {drawerOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setDrawerOpen(false)}
            />
            <div className="absolute inset-y-0 left-0 w-[88vw] max-w-[320px] overflow-y-auto bg-[var(--color-surface)] p-3 shadow-2xl">
              <button
                className="mb-3 inline-flex items-center gap-2 text-[12px] text-[var(--color-fg-muted)]"
                onClick={() => setDrawerOpen(false)}
              >
                <X size={14} /> Закрыть
              </button>
              <SidebarCard
                data={indexQ.data}
                sections={filteredSections}
                selectedId={effectiveSelected}
                onSelect={onSelect}
                query={query}
                onQuery={setQuery}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <main className="min-w-0">
          {sectionQ.isLoading && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-[13px] text-[var(--color-fg-muted)]">
              Загрузка раздела…
            </div>
          )}
          {sectionQ.isError && (
            <div className="rounded-xl border border-[var(--color-danger)] bg-[var(--color-danger-bg)] p-4 text-[13px] text-[var(--color-danger)]">
              Не удалось загрузить раздел. Возможно, на бэке не запущен{" "}
              <code>/api/v1/docs</code>. Запустите{" "}
              <code>python -m astramarket.docs.generate</code> локально, чтобы
              сгенерировать <code>docs/docs.json</code>.
            </div>
          )}
          {sectionQ.data && <SectionView section={sectionQ.data} query={query} />}
        </main>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sidebar
// ────────────────────────────────────────────────────────────────────────────

function SidebarCard({
  data,
  sections,
  selectedId,
  onSelect,
  query,
  onQuery,
}: {
  data: DocsIndex | undefined;
  sections: DocsSectionSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  query: string;
  onQuery: (v: string) => void;
}) {
  const groups = useMemo(() => {
    const api = sections.filter((s) => s.kind === "api");
    const services = sections.filter((s) => s.kind === "service");
    const models = sections.filter((s) => s.kind === "model");
    const meta = sections.filter((s) => s.kind === "meta");
    return { api, services, models, meta };
  }, [sections]);

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
      <label className="relative block">
        <Search
          size={13}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-fg-faint)]"
        />
        <input
          className="block w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] py-2 pl-7 pr-2 text-[12.5px] text-[var(--color-fg)] placeholder:text-[var(--color-fg-faint)] focus:border-[var(--color-fg-muted)] focus:outline-none"
          placeholder="Поиск по разделам / путям"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
      </label>

      <Group
        icon={Code}
        title="Эндпойнты по группам"
        items={groups.api}
        selectedId={selectedId}
        onSelect={onSelect}
      />
      <Group
        icon={Boxes}
        title="Бизнес-сервисы"
        items={groups.services}
        selectedId={selectedId}
        onSelect={onSelect}
      />
      <Group
        icon={Database}
        title="Модели данных"
        items={groups.models}
        selectedId={selectedId}
        onSelect={onSelect}
      />
      {groups.meta.length > 0 && (
        <Group
          icon={Server}
          title="Архитектура"
          items={groups.meta}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      )}

      {data && (
        <div className="mt-3 border-t border-[var(--color-border)] pt-3 text-[11px] text-[var(--color-fg-faint)]">
          {data.endpoints.length} эндпойнтов · {data.services.length} сервисов ·{" "}
          {data.models.length} моделей
        </div>
      )}
    </div>
  );
}

function Group({
  icon: Icon,
  title,
  items,
  selectedId,
  onSelect,
}: {
  icon: typeof BookOpen;
  title: string;
  items: DocsSectionSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);
  if (items.length === 0) return null;
  return (
    <div className="mt-3">
      <button
        className="flex w-full items-center gap-2 px-1 text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Icon size={12} />
        <span className="flex-1 text-left">{title}</span>
        <span className="text-[10px] text-[var(--color-fg-faint)]">{items.length}</span>
      </button>
      {open && (
        <ul className="mt-1.5 space-y-0.5">
          {items.map((s) => (
            <li key={s.id}>
              <button
                onClick={() => onSelect(s.id)}
                className={
                  "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] transition-colors " +
                  (s.id === selectedId
                    ? "bg-[var(--color-surface-3)] font-medium text-[var(--color-fg)]"
                    : "text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-fg)]")
                }
              >
                <span className="truncate">{s.title}</span>
                <span className="shrink-0 rounded-full bg-[var(--color-surface-2)] px-1.5 text-[10.5px] text-[var(--color-fg-faint)]">
                  {s.count}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section content
// ────────────────────────────────────────────────────────────────────────────

function SectionView({ section, query }: { section: DocsSection; query: string }) {
  if (section.kind === "api") return <ApiSection section={section} query={query} />;
  if (section.kind === "service") return <ServiceSection section={section} />;
  if (section.kind === "model") return <ModelSection section={section} />;
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-[13px] text-[var(--color-fg-muted)]">
      Содержимое раздела недоступно.
    </div>
  );
}

function SectionHeader({
  title,
  anchor,
  subtitle,
}: {
  title: string;
  anchor: string;
  subtitle?: string;
}) {
  return (
    <h2
      id={anchor}
      className="group flex items-baseline gap-2 text-[18px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]"
    >
      <a
        href={`#${anchor}`}
        className="text-[var(--color-fg-faint)] opacity-0 transition-opacity group-hover:opacity-100"
        onClick={(e) => {
          e.preventDefault();
          history.replaceState(null, "", `#${anchor}`);
        }}
        aria-label="anchor"
      >
        #
      </a>
      <span>{title}</span>
      {subtitle && (
        <span className="text-[12.5px] font-normal text-[var(--color-fg-muted)]">
          · {subtitle}
        </span>
      )}
    </h2>
  );
}

// ──────── API section
function ApiSection({ section, query }: { section: DocsSection; query: string }) {
  const items = useMemo(() => {
    const t = query.trim().toLowerCase();
    if (!t) return section.endpoints;
    return section.endpoints.filter(
      (e) =>
        e.path.toLowerCase().includes(t) ||
        e.summary.toLowerCase().includes(t) ||
        e.description.toLowerCase().includes(t),
    );
  }, [section.endpoints, query]);
  return (
    <div className="space-y-3">
      <SectionHeader
        title={section.title}
        anchor={`section-${section.id.replace(/[:.]/g, "-")}`}
        subtitle={`${items.length} эндпойнтов`}
      />
      {items.length === 0 ? (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-[13px] text-[var(--color-fg-muted)]">
          Нет эндпойнтов под текущий фильтр.
        </div>
      ) : (
        items.map((e) => <EndpointCard key={`${e.method}-${e.path}`} ep={e} />)
      )}
    </div>
  );
}

const METHOD_COLORS: Record<string, string> = {
  GET: "bg-[var(--color-success-bg)] text-[var(--color-success)]",
  POST: "bg-[#e0ecff] text-[#1d4ed8] dark:bg-[rgba(37,99,235,0.18)] dark:text-[#60a5fa]",
  PUT: "bg-[var(--color-warning-bg)] text-[var(--color-warning)]",
  PATCH: "bg-[var(--color-warning-bg)] text-[var(--color-warning)]",
  DELETE: "bg-[var(--color-danger-bg)] text-[var(--color-danger)]",
};

function MethodBadge({ method }: { method: string }) {
  const cls = METHOD_COLORS[method.toUpperCase()] ?? "bg-[var(--color-surface-2)] text-[var(--color-fg-muted)]";
  return (
    <span
      className={
        "inline-flex h-6 min-w-[58px] items-center justify-center rounded-md px-2 text-[10.5px] font-bold uppercase tracking-[0.04em] " +
        cls
      }
    >
      {method.toUpperCase()}
    </span>
  );
}

function PathView({ path }: { path: string }) {
  // подсвечиваем {pkg} и подобные плейсхолдеры
  const parts = path.split(/(\{[^}]+\})/g);
  return (
    <code className="font-mono text-[12.5px] text-[var(--color-fg)]">
      {parts.map((p, i) =>
        p.startsWith("{") ? (
          <span
            key={i}
            className="rounded bg-[var(--color-brand)]/10 px-1 text-[var(--color-brand)]"
          >
            {p}
          </span>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </code>
  );
}

function EndpointCard({ ep }: { ep: DocsEndpoint }) {
  const [open, setOpen] = useState(false);
  return (
    <article className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] transition-colors hover:border-[var(--color-fg-muted)]">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
      >
        <MethodBadge method={ep.method} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <PathView path={ep.path} />
            {ep.deprecated && (
              <span className="rounded bg-[var(--color-danger-bg)] px-1.5 text-[10px] font-medium text-[var(--color-danger)]">
                deprecated
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[12.5px] text-[var(--color-fg-muted)]">
            {ep.summary || ep.operation_id}
          </div>
        </div>
        {open ? (
          <ChevronDown size={14} className="mt-1 text-[var(--color-fg-muted)]" />
        ) : (
          <ChevronRight size={14} className="mt-1 text-[var(--color-fg-muted)]" />
        )}
      </button>
      {open && (
        <div className="space-y-4 border-t border-[var(--color-border)] px-4 py-3">
          {ep.description && (
            <MultilineText text={ep.description} className="text-[13px] text-[var(--color-fg)]" />
          )}

          {ep.parameters.length > 0 && (
            <div>
              <SubHead>Параметры</SubHead>
              <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
                <table className="w-full border-collapse text-[12.5px]">
                  <thead className="bg-[var(--color-surface-2)] text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Имя</th>
                      <th className="px-3 py-2 text-left font-medium">Где</th>
                      <th className="px-3 py-2 text-left font-medium">Обяз.</th>
                      <th className="px-3 py-2 text-left font-medium">Тип</th>
                      <th className="px-3 py-2 text-left font-medium">Описание</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ep.parameters.map((p) => (
                      <tr key={p.name + p.location} className="border-t border-[var(--color-border)]">
                        <td className="px-3 py-1.5 font-mono text-[12px] text-[var(--color-fg)]">{p.name}</td>
                        <td className="px-3 py-1.5 text-[var(--color-fg-muted)]">{p.location}</td>
                        <td className="px-3 py-1.5">
                          {p.required ? (
                            <span className="text-[var(--color-danger)]">да</span>
                          ) : (
                            <span className="text-[var(--color-fg-muted)]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-[11.5px] text-[var(--color-fg-muted)]">
                          {(p.schema as { type?: string })?.type ?? "—"}
                        </td>
                        <td className="px-3 py-1.5 text-[var(--color-fg-muted)]">{p.description ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ep.request_body_example !== null && ep.request_body_example !== undefined && (
            <div>
              <SubHead>Пример тела запроса</SubHead>
              <Pre>{JSON.stringify(ep.request_body_example, null, 2)}</Pre>
            </div>
          )}

          {ep.responses.length > 0 && (
            <div>
              <SubHead>Ответы</SubHead>
              <ul className="space-y-2">
                {ep.responses.map((r) => (
                  <li key={r.status_code} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-2.5">
                    <div className="flex items-center gap-2 text-[12px]">
                      <StatusPill code={r.status_code} />
                      <span className="text-[var(--color-fg)]">{r.description}</span>
                    </div>
                    {r.example !== null && r.example !== undefined && r.status_code.startsWith("2") && (
                      <Pre className="mt-2">{JSON.stringify(r.example, null, 2)}</Pre>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <SubHead>curl</SubHead>
            <Pre>{ep.curl_example}</Pre>
          </div>
          <div>
            <SubHead>Python</SubHead>
            <Pre>{ep.python_snippet}</Pre>
          </div>
        </div>
      )}
    </article>
  );
}

function StatusPill({ code }: { code: string }) {
  const n = parseInt(code, 10) || 0;
  const cls =
    n >= 500
      ? "bg-[var(--color-danger-bg)] text-[var(--color-danger)]"
      : n >= 400
        ? "bg-[var(--color-warning-bg)] text-[var(--color-warning)]"
        : "bg-[var(--color-success-bg)] text-[var(--color-success)]";
  return (
    <span className={"rounded-full px-2 py-0.5 font-mono text-[11px] font-semibold " + cls}>
      {code}
    </span>
  );
}

function SubHead({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
      {children}
    </div>
  );
}

function Pre({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <pre
      className={
        "overflow-x-auto rounded-lg bg-[var(--color-surface-2)] p-3 font-mono text-[11.5px] leading-snug text-[var(--color-fg)] " +
        className
      }
    >
      {children}
    </pre>
  );
}

function MultilineText({ text, className = "" }: { text: string; className?: string }) {
  // Минимальный markdown-рендер: разделяем по абзацам, рендерим inline-`code`.
  const blocks = text.split(/\n\n+/);
  return (
    <div className={className + " space-y-2"}>
      {blocks.map((b, i) => (
        <p key={i} className="whitespace-pre-wrap">
          {renderInline(b)}
        </p>
      ))}
    </div>
  );
}

function renderInline(s: string): React.ReactNode[] {
  // ` … ` и ** … **
  const out: React.ReactNode[] = [];
  const re = /(`[^`]+`|\*\*[^*]+\*\*)/g;
  let lastIdx = 0;
  let i = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    if (m.index > lastIdx) out.push(s.slice(lastIdx, m.index));
    const tok = m[0];
    if (tok.startsWith("`")) {
      out.push(
        <code key={i++} className="rounded bg-[var(--color-surface-2)] px-1 font-mono text-[12px]">
          {tok.slice(1, -1)}
        </code>,
      );
    } else {
      out.push(
        <strong key={i++} className="font-semibold text-[var(--color-fg)]">
          {tok.slice(2, -2)}
        </strong>,
      );
    }
    lastIdx = m.index + tok.length;
  }
  if (lastIdx < s.length) out.push(s.slice(lastIdx));
  return out;
}

// ──────── Service section
function ServiceSection({ section }: { section: DocsSection }) {
  return (
    <div className="space-y-4">
      <SectionHeader
        title={section.title}
        anchor={`section-${section.id.replace(/[:.]/g, "-")}`}
        subtitle="бизнес-сервис"
      />
      {section.services.map((s) => (
        <ServiceCard key={s.module + s.class_name} svc={s} />
      ))}
    </div>
  );
}

function ServiceCard({ svc }: { svc: DocsService }) {
  return (
    <article className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <h3 className="text-[15px] font-semibold text-[var(--color-fg)]">
        <code className="font-mono">{svc.class_name}</code>
        <span className="ml-2 text-[12px] font-normal text-[var(--color-fg-muted)]">
          {svc.module}
        </span>
      </h3>
      {svc.docstring && (
        <MultilineText
          text={svc.docstring}
          className="mt-2 text-[13px] text-[var(--color-fg-muted)]"
        />
      )}
      <div className="mt-3 space-y-3">
        {svc.methods
          .filter((m) => m.is_public)
          .map((m) => (
            <div key={m.name} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
              <div className="font-mono text-[12.5px] text-[var(--color-fg)]">
                {m.name}
                <span className="text-[var(--color-fg-muted)]">{m.signature}</span>
              </div>
              {m.docstring ? (
                <MultilineText
                  text={m.docstring}
                  className="mt-1.5 text-[12.5px] text-[var(--color-fg-muted)]"
                />
              ) : (
                <div className="mt-1 text-[12px] italic text-[var(--color-fg-faint)]">
                  Без docstring'а
                </div>
              )}
            </div>
          ))}
      </div>
    </article>
  );
}

// ──────── Model section
function ModelSection({ section }: { section: DocsSection }) {
  return (
    <div className="space-y-4">
      <SectionHeader
        title={section.title}
        anchor={`section-${section.id.replace(/[:.]/g, "-")}`}
        subtitle="модель данных"
      />
      {section.models.map((m) => (
        <ModelCard key={m.module + m.class_name} model={m} />
      ))}
    </div>
  );
}

function ModelCard({ model }: { model: DocsModel }) {
  return (
    <article className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <h3 className="text-[15px] font-semibold text-[var(--color-fg)]">
        <code className="font-mono">{model.class_name}</code>
        <span className="ml-2 text-[12px] font-normal text-[var(--color-fg-muted)]">
          таблица <code className="font-mono">{model.table_name}</code>
        </span>
      </h3>
      {model.docstring && (
        <MultilineText
          text={model.docstring}
          className="mt-2 text-[13px] text-[var(--color-fg-muted)]"
        />
      )}
      <div className="mt-3 overflow-x-auto rounded-lg border border-[var(--color-border)]">
        <table className="w-full border-collapse text-[12.5px]">
          <thead className="bg-[var(--color-surface-2)] text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Колонка</th>
              <th className="px-3 py-2 text-left font-medium">Тип</th>
              <th className="px-3 py-2 text-left font-medium">NULL</th>
              <th className="px-3 py-2 text-left font-medium">PK</th>
              <th className="px-3 py-2 text-left font-medium">FK</th>
              <th className="px-3 py-2 text-left font-medium">Index</th>
            </tr>
          </thead>
          <tbody>
            {model.columns.map((c) => (
              <tr key={c.name} className="border-t border-[var(--color-border)]">
                <td className="px-3 py-1.5 font-mono text-[12px] text-[var(--color-fg)]">{c.name}</td>
                <td className="px-3 py-1.5 font-mono text-[11.5px] text-[var(--color-fg-muted)]">{c.type}</td>
                <td className="px-3 py-1.5 text-[var(--color-fg-muted)]">{c.nullable ? "да" : "нет"}</td>
                <td className="px-3 py-1.5">
                  {c.primary_key && (
                    <span className="rounded bg-[var(--color-brand)]/10 px-1.5 text-[10.5px] font-semibold text-[var(--color-brand)]">
                      PK
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 font-mono text-[11.5px] text-[var(--color-fg-muted)]">
                  {c.foreign_key || ""}
                </td>
                <td className="px-3 py-1.5 text-[var(--color-fg-muted)]">{c.indexed ? "да" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// helpers
// ────────────────────────────────────────────────────────────────────────────

function formatGenerated(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
